import { Col, FormInput, FormRadioGroup, Row, InPageAlert } from '@snsw/react-component-library';
import React, { useEffect, useState } from 'react';
import { isEmpty, validateExt } from 'shared/utils/Validation';
import styles from '../PlantItemDetails.module.scss';
import { RadioOrDropdownSelector } from 'shared/components/selector/RadioOrDropdownSelector';
import { RefData } from 'plantItemNew/constants/refData';
import { PlantItemDetailsPage } from 'plantItemNew/constants/plantItemDetailsPage';
import { PlantDesignRefData } from 'plantDesign/constants/PlantDesignRefData';

export const PressureVessel = React.forwardRef((props, ref) => {
  const { value: savedPlantDetails = {}, onChange = () => {}, refData = {}, onErrorChange = () => {} } = props;

  const {
    subType: savedSubType = '',
    otherSubType: savedOtherSubType = '',
    manufacturer: savedManufacturer = '',
    serialNumber: savedSerialNumber = '',
    modelNameOrNumber: savedModelNameOrNumber = '',
    yearOfManufacture: savedYearOfManufacture = '',
    locationType: savedLocationType = '',
    specificLocation: savedSpecificLocation = '',
    uniqueIdentifier: savedUniqueIdentifier = '',
  } = savedPlantDetails;

  const subTypeOptions = Array.isArray(refData[RefData.pressureVessel.subTypeOptions])
    ? refData[RefData.pressureVessel.subTypeOptions].map((e) => ({
        value: e.value,
        text: e.description,
      }))
    : [];

  const itemLocationTypeOptions = Array.isArray(refData[RefData.itemLocationTypeOptions])
    ? refData[RefData.itemLocationTypeOptions].map((e) => ({
        value: e.value,
        label: e.description,
      }))
    : [];

  const [subType, setSubType] = useState(savedSubType);
  const [otherSubType, setOtherSubType] = useState(savedOtherSubType);
  const [manufacturer, setManufacturer] = useState(savedManufacturer);
  const [modelNameOrNumber, setModelNameOrNumber] = useState(savedModelNameOrNumber);
  const [yearOfManufacture, setYearOfManufacture] = useState(savedYearOfManufacture);
  const [serialNumber, setSerialNumber] = useState(savedSerialNumber);
  const [uniqueIdentifier, setUniqueIdentifier] = useState(savedUniqueIdentifier);
  const [locationType, setLocationType] = useState(savedLocationType);

  const [specificLocation, setSpecificLocation] = useState(savedSpecificLocation);

  const [showErrors, setShowErrors] = useState(false);
  const [errors, setErrors] = useState({});

  const [errorSummary, setErrorSummary] = useState([]);

  const validators = [
    {
      fieldName: 'subType',
      fieldValue: subType,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: 'subType_other',
      fieldValue: otherSubType,
      isValid: (v) =>
        !isEmpty(v) || subType !== PlantDesignRefData.plantRefs.pressureVessel.subTypeRef.otherSubTypeValue,
      errorMessage: () => 'Please enter details',
    },
    {
      fieldName: 'manufacturer',
      fieldValue: manufacturer,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please enter details',
    },
    {
      fieldName: 'modelNameOrNumber',
      fieldValue: modelNameOrNumber,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please enter details',
    },
    {
      fieldName: 'yearOfManufacture',
      fieldValue: yearOfManufacture,
      isValid: (v) => !isEmpty(v) && Number.isInteger(+v),
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter a valid year'),
    },
    {
      fieldName: 'serialNumber',
      fieldValue: serialNumber,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please enter details',
    },
    {
      fieldName: 'locationType',
      fieldValue: locationType,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
  ];

  if (ref) {
    ref.current = {
      validate: () => {
        let isCurrentComponentValid = validateExt(validators, setErrors, setErrorSummary);

        setShowErrors(!isCurrentComponentValid);

        return isCurrentComponentValid;
      },
    };
  }

  useEffect(() => {
    let itemDetails = {
      subType,
      otherSubType,
      manufacturer,
      serialNumber,
      modelNameOrNumber,
      yearOfManufacture,
      locationType,
      specificLocation,
      uniqueIdentifier,
    };
    onChange(itemDetails);

    if (showErrors) validateExt(validators, setErrors, setErrorSummary);
  }, [
    subType,
    otherSubType,
    manufacturer,
    modelNameOrNumber,
    yearOfManufacture,
    serialNumber,
    uniqueIdentifier,
    locationType,
    specificLocation,
  ]);

  useEffect(() => {
    onErrorChange(errorSummary);
  }, [errorSummary]);

  return (
    <>
      <Row>
        <Col span={8}>
          <InPageAlert
            variant="info"
            title="Pressure vessels; categorised as hazard level A, B or C according to Section 2.1 of AS 4343: 2014 Pressure equipment—Hazard levels must be item registered."
            headingLevel="h4"
          >
            The following are excluded from requiring item registration:
            <ul style={{ marginLeft: 5, paddingLeft: 10 }}>
              <li>Gas cylinders</li>
              <li>LP Gas fuel vessels for automotive use</li>
              <li>Serially produced vessels</li>
              <li>
                Pressure equipment excluded from the scope of AS/NZS 1200:2015 <i>Pressure equipment</i> (see paragraph
                A3 of Appendix A)
              </li>
              <li>
                Pressure vessels that do not require periodic internal inspection in accordance with the criteria in
                Table 4.1 in AS/NZS 3788:2006 <i>Pressure equipment—In-service inspection</i>.
              </li>
            </ul>
          </InPageAlert>

          <RadioOrDropdownSelector
            id="subType"
            options={subTypeOptions}
            title={PlantItemDetailsPage.itemDetails.pressureVesselTypeLabel}
            errorMessage={errors?.subType?.errorMessage}
            hasError={showErrors && errors?.subType?.invalid}
            onChange={(v) => {
              setSubType(v);
              setOtherSubType('');
            }}
            value={subType}
            showExtraInput={subType === PlantDesignRefData.plantRefs.pressureVessel.subTypeRef.otherSubTypeValue}
            onChangeExtraInput={setOtherSubType}
            extraInputValue={otherSubType}
            extraInputErrorMessage={errors?.subType_other?.errorMessage}
            extraInputHasError={showErrors && errors?.subType_other?.invalid}
            extraInputTitle={'Other please describe'}
            margin={{ top: 'md' }}
            inputWidth="lg"
            maxOptionsLengthForRadio={2}
          />

          <FormInput
            id="manufacturer"
            label={PlantItemDetailsPage.itemDetails.manufacturerLabel}
            errorMessage={errors?.manufacturer?.errorMessage}
            hasError={showErrors && errors?.manufacturer?.invalid}
            value={manufacturer}
            onChange={(e, { value }) => setManufacturer(value)}
            inputWidth="lg"
            margin={{ top: 'md' }}
          />

          <FormInput
            label={PlantItemDetailsPage.itemDetails.modelNameOrNumberLabel}
            helpMessage="Supplier’s model name or number can be provided"
            id="modelNameOrNumber"
            errorMessage={errors?.modelNameOrNumber?.errorMessage}
            hasError={showErrors && errors?.modelNameOrNumber?.invalid}
            value={modelNameOrNumber}
            onChange={(e, { value }) => setModelNameOrNumber(value)}
            inputWidth="lg"
            margin={{ top: 'md' }}
          />

          <FormInput
            label={PlantItemDetailsPage.itemDetails.yearOfManufactureLabel}
            id="yearOfManufacture"
            errorMessage={errors?.yearOfManufacture?.errorMessage}
            helpMessage="If unknown, enter an approximate year of manufacture"
            hasError={showErrors && errors?.yearOfManufacture?.invalid}
            value={yearOfManufacture}
            onChange={(e, { value }) => setYearOfManufacture(value)}
            inputWidth="xs"
            placeholder="YYYY"
            mask={[/\d/, /\d/, /\d/, /\d/]}
            margin={{ top: 'md' }}
          />

          <div className={`${styles.labelMsg} ${styles.marginTopOneHalf}`}>
            {PlantItemDetailsPage.itemDetails.serialNumberLabel}
          </div>
          <div className={styles.helpMsg}>
            Unique identifying number issued by the manufacturer. <br />
            Note: Lifts, escalators and moving walks may have a contract number.
          </div>
          <FormInput
            id="serialNumber"
            value={serialNumber}
            onChange={(e, { value }) => setSerialNumber(value)}
            errorMessage={errors?.serialNumber?.errorMessage}
            hasError={showErrors && errors?.serialNumber?.invalid}
            inputWidth="lg"
            margin={{ top: 'none' }}
          />

          <FormRadioGroup
            id="locationType"
            name="locationType"
            legend={PlantItemDetailsPage.itemDetails.locationTypeLabel}
            vertical={true}
            options={itemLocationTypeOptions}
            onChange={setLocationType}
            value={locationType}
            errorMessage={errors?.locationType?.errorMessage}
            hasError={showErrors && errors?.locationType?.invalid}
            margin={{ top: 'md' }}
          />

          {locationType && (
            <>
              <FormInput
                label={PlantItemDetailsPage.itemDetails.specificLocationLabel}
                helpMessage="This is to help you identify where your item is specifically located at your address"
                placeholder="eg. Building name or room the item is stored in."
                value={specificLocation}
                onChange={(e, { value }) => setSpecificLocation(value)}
                margin={{ top: 'md' }}
              />

              <FormInput
                label={PlantItemDetailsPage.itemDetails.uniqueIdentifierLabel}
                helpMessage="You can provide your own unique reference number to help identify the item"
                value={uniqueIdentifier}
                onChange={(e, { value }) => setUniqueIdentifier(value)}
                inputWidth="lg"
                margin={{ top: 'md' }}
              />
            </>
          )}
        </Col>
      </Row>
    </>
  );
});

PressureVessel.displayName = 'PressureVessel';
